import JsBarcode from 'jsbarcode';
import React, { FC, memo, useEffect, useMemo, useRef } from 'react';
import { PresenterProps } from 'src/components/PickingListComponent';
import {
  PickingList, ShipperList, UnloadList, ItemList,
  PICKING_PAGE_ROWS_DEFAULT,
  PickingSortMode,
  DEFAULT_PICKING_SORT_MODE,
  getSlipNumbers,
  pickingPageRowsForBarcodes,
} from 'src/entities/pickingListEntity';

import style from './print.module.css';

function PickingHeader() {
  return (
    <>
      <div className={style.picking_title_wrapper}>
        <h1 className={style.picking_title}>ピッキングリスト</h1>
      </div>
      <div className={style.flex_area}>
        <div className={style.signature_area}>
          <div className={style.signature_box}>
            <div className={style.signature_header}>仕分け作業者</div>
            <div className={style.signature_body} />
          </div>
          <div className={style.signature_box}>
            <div className={style.signature_header}>検品者</div>
            <div className={style.signature_dual_body}>
              <div className={style.signature_dual_cell} />
              <div className={style.signature_dual_cell} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className={style.delivery_row}>
      <div className={style.delivery_label}>{label}</div>
      <div className={style.delivery_value}>{value}</div>
    </div>
  );
}

function Barcode({ value }: { value: string }) {
  const ref = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!ref.current || !value) return;

    try {
      JsBarcode(ref.current, value, {
        format: 'CODE128',
        width: 1.6,
        height: 28,
        displayValue: true,
        fontSize: 9,
        margin: 0,
      });
    } catch {
      // バーコード化できない値でも帳票全体の表示は継続する。
    }
  }, [value]);

  return <svg ref={ref} className={style.barcode} data-testid="slip-barcode" />;
}

function SlipNumberValue({ unload, barcode }: { unload: UnloadList; barcode: boolean }) {
  const slipNumbers = getSlipNumbers(unload);

  if (!barcode) return <>{unload.slip_numbers ?? ''}</>;

  return (
    <div className={style.barcode_list}>
      {slipNumbers.map((slipNumber) => (
        <Barcode value={slipNumber} key={slipNumber} />
      ))}
    </div>
  );
}

function DriverInfo({ unload, shipper, barcode }: { unload: UnloadList; shipper: ShipperList; barcode: boolean }) {
  return (
    <div className={style.delivery_info}>
      <div className={style.delivery_row_grid}>
        <InfoRow label="積込日" value={unload.items?.[0]?.loading_time_range_start ?? ''} />
        <InfoRow label="納品日" value={unload.items?.[0]?.unloading_time_range_start ?? ''} />
      </div>
      <InfoRow label="荷主名" value={shipper.shipper_name} />
      <InfoRow label="納品先名" value={unload.unloading_name} />
      <InfoRow label="納品先住所" value={unload.items?.[0]?.unloading_address ?? ''} />
      <InfoRow label="伝票No" value={<SlipNumberValue unload={unload} barcode={barcode} />} />
    </div>
  );
}

function GoodsTable({ displayItems, showSummary, summaryTotal }: { displayItems: ItemList[]; showSummary: boolean; summaryTotal: number }) {
  return (
    <table className={style.goods_table}>
      <thead>
        <tr className={style.header}>
          <th className={style.col_item_code} style={{ textWrap: 'nowrap' }}>商品CD</th>
          <th className={style.col_item_name} style={{ textWrap: 'nowrap' }}>商品名</th>
          <th className={style.col_item_lot} style={{ textWrap: 'nowrap' }}>ロット</th>
          <th className={style.col_item_qty} style={{ textWrap: 'nowrap' }}>数量</th>
          <th className={style.col_item_packing} style={{ textWrap: 'nowrap' }}>荷姿</th>
          <th className={style.col_item_memo} style={{ textWrap: 'nowrap' }}>備考</th>
        </tr>
      </thead>
      <tbody>
        {displayItems.map((row, index) => (
          <tr className={style.goods} key={`${index}-${row.item_code || 'empty'}-${row.empty_id ?? 'item'}`}>
            <td className={`${style.text_center} ${style.col_item_code}`}>{row.item_code}</td>
            <td className={`${style.text_left} ${style.col_item_name}`}>{row.item_name}</td>
            <td className={style.col_item_lot}>{row.item_lot_number ?? ''}</td>
            <td className={`${style.text_center} ${style.col_item_qty}`}>{row.item_count !== 0 ? row.item_count : ''}</td>
            <td className={`${style.text_center} ${style.col_item_packing}`}>{row.item_packing_style}</td>
            <td className={style.col_item_memo} />
          </tr>
        ))}
        {showSummary && (
          <tr className={style.summery_row}>
            <td className={style.summery} colSpan={3}>合計</td>
            <td className={style.summery}>{summaryTotal !== 0 ? summaryTotal : ''}</td>
            {/* 荷姿・備考の2列分。テーブル全6列の罫線を揃えるための空セル */}
            <td className={style.summery} colSpan={2} />
          </tr>
        )}
      </tbody>
    </table>
  );
}

function PickingFooter({
  courseNumber,
  driver,
  currentPage,
  totalPage,
}: {
  courseNumber?: string | null;
  driver: PickingList['driver'];
  currentPage: number;
  totalPage: number;
}) {
  const hasMain = courseNumber != null || !!driver?.name;
  return (
    <div className={style.footer}>
      {hasMain && (
        <div className={style.footer_main}>
          {courseNumber != null && <div className={style.dispatch_box}>ｺｰｽ:{courseNumber}</div>}
          {driver?.name && <div className={style.driver_box}>{driver.name}</div>}
        </div>
      )}
      <div className={style.issue}>
        {`${formatDateTime(new Date())} 発行 `}
        {`<${currentPage}/${totalPage}>`}
      </div>
    </div>
  );
}

function formatDateTime(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${y}/${m}/${d} ${hh}:${mm}`;
}

function makeEmptyRows(count: number): ItemList[] {
  return Array.from({ length: Math.max(0, count) }, (_, i) => ({
    item_carrier_company: '',
    item_code: '',
    item_count: 0,
    item_name: '',
    item_packing_style: '',
    loading_time_range_start: '',
    unloading_address: '',
    unloading_time_range_start: '',
    empty_id: i,
  }));
}

// 商品行を rowsPerPage 行ごとに分割。最終/単一ページのみパディングする。
export function buildPickingPages(items: ItemList[], rowsPerPage: number): ItemList[][] {
  if (items.length === 0) return [makeEmptyRows(rowsPerPage)];
  const pages: ItemList[][] = [];
  for (let i = 0; i < items.length; i += rowsPerPage) {
    const chunk = items.slice(i, i + rowsPerPage);
    const isLast = i + rowsPerPage >= items.length;
    pages.push(isLast ? [...chunk, ...makeEmptyRows(rowsPerPage - chunk.length)] : chunk);
  }
  return pages;
}

// レンダリング用に展開済みの 1 ページ分の情報。
type PickingPage = {
  key: string;
  course: PickingList;
  shipper: ShipperList;
  unload: UnloadList;
  barcode: boolean;
  pageItems: ItemList[];
  isLast: boolean;
  summaryTotal: number;
};

// 1 納品先 = 1 leaf。並び替えの最小単位（複数ページの納品先は 1 leaf にまとまる）。
type PickingLeaf = {
  courseIdx: number;
  course: PickingList;
  dispatchIdx: number;
  shipperIdx: number;
  unloadIdx: number;
  shipper: ShipperList;
  unload: UnloadList;
  isEmpty: boolean;
};

function buildPickingLeaves(models: PickingList[]): PickingLeaf[] {
  const leaves: PickingLeaf[] = [];
  models.forEach((course, courseIdx) => {
    if (course.dispatches.length === 0) {
      leaves.push({
        courseIdx,
        course,
        dispatchIdx: -1,
        shipperIdx: -1,
        unloadIdx: -1,
        shipper: { shipper_name: '', unloads: [] },
        unload: { unloading_name: '', items: [] },
        isEmpty: true,
      });
      return;
    }
    course.dispatches.forEach((loadDate, dispatchIdx) => {
      loadDate.shippers.forEach((shipper, shipperIdx) => {
        shipper.unloads.forEach((unload, unloadIdx) => {
          leaves.push({ courseIdx, course, dispatchIdx, shipperIdx, unloadIdx, shipper, unload, isEmpty: false });
        });
      });
    });
  });
  return leaves;
}

// shipper-course: 荷主名の五十音順 → コース番号(courseIdx) → 積込日 → 納品先。空 leaf は末尾。
// course-shipper: API が返した自然順のまま（Array.sort は安定なので何もしない）。
function sortPickingLeaves(leaves: PickingLeaf[], sortMode: PickingSortMode): PickingLeaf[] {
  if (sortMode !== 'shipper-course') return leaves;
  return [...leaves].sort((a, b) => {
    if (a.isEmpty !== b.isEmpty) return a.isEmpty ? 1 : -1;
    // shipper_name は型上は string だが、API 実データで null/空になり得る（buildPickingShipperOptions も
    // falsy を除外している）。localeCompare での TypeError を防ぐため空文字にフォールバックする。
    const shipperA = a.shipper.shipper_name ?? '';
    const shipperB = b.shipper.shipper_name ?? '';
    const byShipper = shipperA.localeCompare(shipperB, 'ja');
    if (byShipper !== 0) return byShipper;
    if (a.courseIdx !== b.courseIdx) return a.courseIdx - b.courseIdx;
    if (a.dispatchIdx !== b.dispatchIdx) return a.dispatchIdx - b.dispatchIdx;
    return a.unloadIdx - b.unloadIdx;
  });
}

// models をフラットなページ配列に展開する（純粋関数。レンダリング中の副作用を避けるため事前計算する）。
function buildPickingPageList(
  models: PickingList[],
  barcodeShipperSet: Set<string>,
  sortMode: PickingSortMode,
): PickingPage[] {
  const leaves = sortPickingLeaves(buildPickingLeaves(models), sortMode);
  const list: PickingPage[] = [];
  // key は名称・車番が一意とは限らないため、元の階層インデックスで一意性を保証する（並び替え後も不変）
  leaves.forEach((leaf) => {
    if (leaf.isEmpty) {
      list.push({
        key: `${leaf.courseIdx}-empty`,
        course: leaf.course,
        shipper: leaf.shipper,
        unload: leaf.unload,
        barcode: false,
        pageItems: makeEmptyRows(PICKING_PAGE_ROWS_DEFAULT),
        isLast: true,
        summaryTotal: 0,
      });
      return;
    }
    const { course, shipper, unload } = leaf;
    const items = unload.items ?? [];
    const barcode = barcodeShipperSet.has(shipper.shipper_name);
    const slipCount = getSlipNumbers(unload).length;
    const rowsPerPage = pickingPageRowsForBarcodes(slipCount, barcode);
    const pages = buildPickingPages(items, rowsPerPage);
    const summaryTotal = items.reduce((s, it) => s + it.item_count, 0);
    pages.forEach((pageItems, pageIdx) => {
      list.push({
        key: `${leaf.courseIdx}-${leaf.dispatchIdx}-${leaf.shipperIdx}-${leaf.unloadIdx}-${pageIdx}`,
        course,
        shipper,
        unload,
        barcode,
        pageItems,
        isLast: pageIdx === pages.length - 1,
        summaryTotal,
      });
    });
  });
  return list;
}

const PickingListPresenter: FC<PresenterProps> = memo(({ models, date: _date, unit: _unit, barcodeShippers, sortMode = DEFAULT_PICKING_SORT_MODE }) => {
  const barcodeShipperSet = useMemo(() => new Set(barcodeShippers), [barcodeShippers]);
  const pages = useMemo(() => buildPickingPageList(models, barcodeShipperSet, sortMode), [models, barcodeShipperSet, sortMode]);
  const totalPage = pages.length;

  return (
    <>
      <title>ピッキングリスト</title>
      <div className={style.main}>
        {pages.map((page, index) => (
          <div className={style.page} style={{ page: 'A4PickingList' }} key={page.key}>
            <PickingHeader />
            <DriverInfo unload={page.unload} shipper={page.shipper} barcode={page.barcode} />
            <GoodsTable displayItems={page.pageItems} showSummary={page.isLast} summaryTotal={page.summaryTotal} />
            <PickingFooter courseNumber={page.course.course_number} driver={page.course.driver} currentPage={index + 1} totalPage={totalPage} />
          </div>
        ))}
      </div>
    </>
  );
});

export default PickingListPresenter;
